import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { usePushNotifications } from "@/services/push-notifications";
import { useAuth } from "@/hooks/use-auth";
import { useWebSocket } from "@/hooks/use-websocket";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, Settings, Smartphone, CheckCircle, XCircle } from "lucide-react";

export function NotificationSettings() {
  const {
    initialize,
    requestPermission,
    subscribe,
    unsubscribe,
    isSubscribed,
    getSubscription,
    showLocalNotification
  } = usePushNotifications();

  const { user } = useAuth();
  const queryClient = useQueryClient();
  useWebSocket(); // Establish WebSocket connection
  const [isInitialized, setIsInitialized] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Memoize the initialization function to prevent infinite loops
  const initializeNotifications = useCallback(async () => {
    setLoading(true);
    try {
      // Initialize if not already done
      const initialized = await initialize();
      setIsInitialized(initialized);

      // Check current permission
      if ('Notification' in window) {
        setPermission(Notification.permission);
      }

      // Check subscription status
      if (initialized) {
        const subscriptionStatus = await isSubscribed();
        setSubscribed(subscriptionStatus);
      }
    } catch (error) {
      console.error('Error checking notification state:', error);
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - only create once

  // Check initial state
  useEffect(() => {
    initializeNotifications();
  }, [initializeNotifications]);

  // Check subscription state periodically to ensure UI stays in sync with DB
  useEffect(() => {
    const checkSubscriptionPeriodically = setInterval(() => {
      if (isInitialized && subscribed) {
        // Recheck subscription state to ensure it's still valid
        isSubscribed().then(setSubscribed);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkSubscriptionPeriodically);
  }, [isInitialized, subscribed, isSubscribed]);

  const handleEnableNotifications = async () => {
    setLoading(true);
    try {
      // Request permission
      const permissionResult = await requestPermission();
      setPermission(permissionResult);

      // Subscribe if permission granted
      if (permissionResult === 'granted') {
        const subscription = await subscribe();
        setSubscribed(!!subscription);
        
        // Force refetch all event notification preference queries
        queryClient.refetchQueries({ 
          predicate: (query) => {
            const queryKey = query.queryKey[0]?.toString() || '';
            return queryKey.includes('/notification-preference');
          }
        });
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableNotifications = async () => {
    setLoading(true);
    try {
      const success = await unsubscribe();
      if (success) {
        setSubscribed(false);
        
        // Force refetch all event notification preference queries
        queryClient.refetchQueries({ 
          predicate: (query) => {
            const queryKey = query.queryKey[0]?.toString() || '';
            return queryKey.includes('/notification-preference');
          }
        });
      }
    } catch (error) {
      console.error('Error disabling notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTestNotification = () => {
    showLocalNotification({
      title: 'Test Notification',
      body: 'This is a test notification from Jam Night Manager!',
      icon: '/jam-jar.png',
      tag: 'test-notification',
    });
  };

  const getPermissionBadge = () => {
    switch (permission) {
      case 'granted':
        return <Badge variant="default" className="gap-1"><CheckCircle className="h-3 w-3" /> Granted</Badge>;
      case 'denied':
        return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" /> Denied</Badge>;
      default:
        return <Badge variant="secondary" className="gap-1"><Bell className="h-3 w-3" /> Not Asked</Badge>;
    }
  };

  const getSubscriptionBadge = () => {
    if (!isInitialized) {
      return <Badge variant="secondary">Not Available</Badge>;
    }
    
    return subscribed ? (
      <Badge variant="default" className="gap-1"><Bell className="h-3 w-3" /> Active</Badge>
    ) : (
      <Badge variant="secondary" className="gap-1"><BellOff className="h-3 w-3" /> Inactive</Badge>
    );
  };

  if (!isInitialized) {
    let isDev = false;
    try {
      isDev = import.meta.env.DEV;
    } catch (e) {
      isDev = false;
    }
    
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellOff className="h-5 w-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            {isDev 
              ? "Push notifications are not available in development mode. Build and run the production version to test notifications."
              : "Push notifications are not supported in your browser."
            }
          </CardDescription>
        </CardHeader>
        {isDev && (
          <CardContent>
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-800">Development Mode</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    To test push notifications:
                  </p>
                  <ol className="text-sm text-blue-700 mt-2 list-decimal list-inside space-y-1">
                    <li>Run <code>npm run build</code> to build the production version</li>
                    <li>Serve the built files from the <code>dist</code> directory</li>
                    <li>Access the app via HTTPS (required for push notifications)</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Push Notifications
        </CardTitle>
        <CardDescription>
          Receive real-time notifications for important events and updates.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Current Status */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Permission Status</Label>
            {getPermissionBadge()}
          </div>
          <div className="flex items-center justify-between">
            <Label>Subscription Status</Label>
            {getSubscriptionBadge()}
          </div>
        </div>

        {/* Enable/Disable Notifications */}
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="text-base">Enable Notifications</Label>
            <div className="text-sm text-muted-foreground">
              Get notified about stage calls, queue updates, and important event information.
            </div>
          </div>
          <Switch
            checked={subscribed}
            onCheckedChange={subscribed ? handleDisableNotifications : handleEnableNotifications}
            disabled={loading || (permission === 'denied')}
          />
        </div>

        {/* Permission Denied Help */}
        {permission === 'denied' && (
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-start gap-2">
              <XCircle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-orange-800">Notifications Blocked</h4>
                <p className="text-sm text-orange-700 mt-1">
                  You've blocked notifications for this site. To enable them:
                </p>
                <ol className="text-sm text-orange-700 mt-2 list-decimal list-inside space-y-1">
                  <li>Click the lock icon in your browser's address bar</li>
                  <li>Set notifications to "Allow"</li>
                  <li>Refresh the page</li>
                </ol>
              </div>
            </div>
          </div>
        )}

        {/* Notification Types */}
        {subscribed && (
          <div className="space-y-3">
            <Label className="text-base">Notification Types</Label>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">Stage Calls</span>
                </div>
                <Badge variant="outline">Active</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="h-4 w-4 text-green-600" />
                  <span className="text-sm">Queue Updates</span>
                </div>
                <Badge variant="outline">Active</Badge>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">Event Updates</span>
                </div>
                <Badge variant="outline">Active</Badge>
              </div>
            </div>
          </div>
        )}

        {/* Test Notification */}
        {permission === 'granted' && (
          <div className="pt-4 border-t">
            <Button
              onClick={handleTestNotification}
              variant="outline"
              className="w-full"
            >
              <Bell className="h-4 w-4 mr-2" />
              Test Notification
            </Button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-4">
          {!subscribed && permission !== 'denied' && (
            <Button
              onClick={handleEnableNotifications}
              disabled={loading}
              className="flex-1"
            >
              <Bell className="h-4 w-4 mr-2" />
              {loading ? 'Enabling...' : 'Enable Notifications'}
            </Button>
          )}
          
          {subscribed && (
            <Button
              onClick={handleDisableNotifications}
              disabled={loading}
              variant="outline"
              className="flex-1"
            >
              <BellOff className="h-4 w-4 mr-2" />
              {loading ? 'Disabling...' : 'Disable Notifications'}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}